import { ReparoApi } from './index';
import { fetchPdfObjectUrl } from './pdf';
import type {
    BatchRegisterRow,
    BucketedItemBalance,
    DeclarationBlocked,
    DeclarationPreview,
    DeclarationRecorded,
    DeclarationRequest,
    DispatchResult,
    DispatchShipmentRequest,
    ReceivableOrder,
    ReceiveResult,
    SiteLedgerResponse,
    SiteOrder,
    VendorCatalog,
} from './vendor-portal';

/**
 * How recently anything at all was recorded at a site.
 *
 * `NEVER` is deliberately not the worst end of `STALE` — a site that has not started is a
 * different conversation from one that has stopped, and they get chased differently.
 */
export type SiteFreshness = 'FRESH' | 'SLOWING' | 'STALE' | 'NEVER';

export interface CmSiteSummary {
    warehouseId: string;
    name: string;
    vendorId: string | null;
    vendorName: string | null;

    /** The last time anybody recorded anything here — the site OR the brand on its behalf. */
    lastPostedAt: string | null;
    freshness: SiteFreshness;

    /** Share of recent rows the site recorded itself. Null when nothing was recorded at all. */
    adoptionRatio: number | null;
    cmRows: number;
    brandRows: number;

    /** Finished goods only, all three — the site's materials are not summed in. */
    usable: number;
    inQc: number;
    blocked: number;

    openLines: number;

    /** Finished-goods batches waiting on this brand's verdict. */
    pendingFgQc: number;
    /** Material batches received here that the brand still has to say the supplier of. */
    awaitingSupplier: number;

    /** Netted output on the requested day, and the mean over the requested window. */
    producedOnDay: number;
    producedDailyAverage: number;
}

/**
 * Every manufacturing site, worst first.
 *
 * The window is sent by the caller because "today" belongs to whoever is reading — a server
 * deciding it is how a screen ends up a day out for exactly the people furthest from the data.
 */
export const getCmSites = async (window?: {
    day: string;
    fromDate: string;
    toDate: string;
}): Promise<{ sites: CmSiteSummary[] }> =>
    ReparoApi({
        method: 'GET',
        url: window
            ? `/cm-sites?day=${window.day}&fromDate=${window.fromDate}&toDate=${window.toDate}`
            : '/cm-sites',
    });

export interface ProductionDayPoint {
    /** `YYYY-MM-DD`. Text, not a Date — parsing it locally is how a bar lands on the wrong day. */
    day: string;
    quantity: number;
    runs: number;
}

export interface ProductionBreakdownRow {
    key: string;
    label: string;
    quantity: number;
    runs: number;
    skus: number;
    /** Batch grouping only. */
    productName?: string | null;
    siteName?: string | null;
    qcStatus?: 'PENDING' | 'PASSED' | 'FAILED' | null;
    expiryDate?: string | null;
}

export interface ProductionMonitorResult {
    selectedDay: string;
    /** True when the requested day produced nothing and this answers for the last that did. */
    fellBackToLastRun: boolean;
    selectedQuantity: number;
    selectedRuns: number;
    averageDaily: number;
    trend: ProductionDayPoint[];
    breakdown: ProductionBreakdownRow[];
    groupBy: 'product' | 'warehouse' | 'batch';
}

/**
 * What the brand's manufacturers made, per day.
 *
 * One endpoint, two scopes: omit `warehouseId` for every site, pass one for that site.
 */
export const getProductionMonitor = async (params: {
    day: string;
    fromDate: string;
    toDate: string;
    warehouseId?: string;
    groupBy?: 'product' | 'warehouse' | 'batch';
    /** Only when the day was defaulted, never when somebody picked a bar. */
    fallbackToLastRun?: boolean;
}): Promise<ProductionMonitorResult> => {
    const query = new URLSearchParams({
        day: params.day,
        fromDate: params.fromDate,
        toDate: params.toDate,
    });
    if (params.warehouseId) query.set('warehouseId', params.warehouseId);
    if (params.groupBy) query.set('groupBy', params.groupBy);
    if (params.fallbackToLastRun) query.set('fallbackToLastRun', 'true');

    return ReparoApi({ method: 'GET', url: `/cm-sites/production-monitor?${query}` });
};

// ─── One site, read as the brand ───────────────────────────────
//
// Deliberately the same shapes the manufacturer's own screens use. Two readers over one set of
// numbers would eventually disagree, and the first thing a brand does with this screen is check
// it against what the site told them.

export const getSiteStock = async (
    warehouseId: string,
    body: { itemType?: 'material' | 'product'; itemIds?: string[] } = {},
): Promise<{ items: BucketedItemBalance[] }> =>
    ReparoApi({
        method: 'POST',
        url: `/cm-sites/${warehouseId}/stock`,
        body: JSON.stringify(body),
    });

export const getSiteOrdersForBrand = async (
    warehouseId: string,
): Promise<{ orders: SiteOrder[] }> =>
    ReparoApi({ method: 'GET', url: `/cm-sites/${warehouseId}/orders` });

export const getSiteLedgerForBrand = async (
    warehouseId: string,
    body: Record<string, unknown> = {},
): Promise<SiteLedgerResponse> =>
    ReparoApi({
        method: 'POST',
        url: `/cm-sites/${warehouseId}/ledger`,
        body: JSON.stringify(body),
    });

// ─── Writing on a site's behalf ────────────────────────────────
//
// Every one of these carries `reportedBy` — the person at the site whose word it is. The
// account stays the coordinator's, so the ledger keeps saying two separate things.

export interface OnBehalfContext {
    warehouseId: string;
    reportedBy: string;
}

export const declareOnBehalf = async (
    context: OnBehalfContext,
    declaration: DeclarationRequest,
): Promise<DeclarationRecorded | DeclarationBlocked> =>
    ReparoApi({
        method: 'POST',
        url: '/cm-sites/declare',
        body: JSON.stringify({ ...context, declaration }),
    });

/**
 * Send finished goods from a site, as the brand.
 *
 * NO REPORTER, unlike its neighbours here. Every other call on this file records something the
 * site did and names the person there whose word it is. The brand releases finished goods — it
 * passes the batch and then sends it — so there is nobody at the site to name, and the movement
 * is attributed to the brand user who created the shipment. Same shape as markQcOnBehalf, for
 * the same reason.
 */
export const createShipment = async (
    warehouseId: string,
    dispatch: DispatchShipmentRequest,
): Promise<DispatchResult> =>
    ReparoApi({
        method: 'POST',
        url: '/cm-sites/dispatch',
        body: JSON.stringify({ warehouseId, dispatch }),
    });

export const receiveOnBehalf = async (
    context: OnBehalfContext,
    receipt: Record<string, unknown>,
): Promise<ReceiveResult> =>
    ReparoApi({
        method: 'POST',
        url: '/cm-sites/receive',
        body: JSON.stringify({ ...context, receipt }),
    });

/**
 * The brand's own verdict on a batch at a site.
 *
 * Takes the warehouse rather than the full on-behalf context: every other call here carries
 * "reported by", naming the person at the site whose word it is, and a QC verdict has no such
 * person. It is the brand's decision, recorded against the brand user who made it.
 */
export const markQcOnBehalf = async (
    warehouseId: string,
    batchId: string,
    /**
     * `passedQuantity` / `failedQuantity` are sent together or not at all. Sending them records
     * a partial verdict — 100 of 120 accepted — and writes the rejected units off the site's
     * balance in the same transaction. Omitting them keeps the whole-batch verdict.
     */
    verdict: {
        qcStatus: string;
        notes?: string;
        passedQuantity?: number;
        failedQuantity?: number;
    },
): Promise<{ notable: boolean }> =>
    ReparoApi({
        method: 'POST',
        url: '/cm-sites/qc',
        body: JSON.stringify({ warehouseId, batchId, verdict }),
    });

/**
 * Name who sent a batch the site received without a supplier.
 *
 * The site's door asks for none; a material with more than one mapped supplier lands here for
 * the brand to say. Mapped suppliers only. The batch keeps its id; only its origin changes.
 */
export const attributeBatchSupplier = async (
    warehouseId: string,
    batchId: string,
    supplierId: string,
): Promise<unknown> =>
    ReparoApi({
        method: 'POST',
        url: `/cm-sites/${warehouseId}/batches/${batchId}/supplier`,
        body: JSON.stringify({ supplierId }),
    });

/**
 * Undo a movement a site recorded.
 *
 * The reason is required here and optional for the site's own undo, because the two are
 * different acts: a manufacturer fixing its own typo minutes later owes no explanation, a brand
 * reaching into a site's ledger weeks later absolutely does — the site sees it.
 */
export const reverseOnBehalf = async (
    context: OnBehalfContext,
    eventId: string,
    reason: string,
): Promise<{ eventIds: string[]; reversedCount: number }> =>
    ReparoApi({
        method: 'POST',
        url: '/cm-sites/reverse',
        body: JSON.stringify({ ...context, eventId, reason }),
    });

/**
 * Where a site may ship, for the brand acting on its behalf - the same lane-narrowed list
 * the site's own portal offers, from the same rule on the server.
 */
export const getSiteDestinationsForBrand = async (
    warehouseId: string,
): Promise<Array<{ warehouseId: string; name: string }>> =>
    ReparoApi({
        method: 'GET',
        url: `/cm-sites/${warehouseId}/destinations`,
    });

/** The name the next load from this site will get if the field is left blank. */
export const getNextShipmentReferenceForBrand = async (
    warehouseId: string,
): Promise<{ reference: string }> =>
    ReparoApi({ method: 'GET', url: `/cm-sites/${warehouseId}/next-shipment-reference` });

export const getSiteCatalog = async (warehouseId: string): Promise<VendorCatalog> =>
    ReparoApi({ method: 'GET', url: `/cm-sites/${warehouseId}/catalog` });

/** The open purchase orders a delivery at this site can be received against. */
export const getSiteReceivableOrders = async (warehouseId: string): Promise<ReceivableOrder[]> =>
    ReparoApi({ method: 'GET', url: `/cm-sites/${warehouseId}/receivable-orders` });

export const previewDeclarationOnBehalf = async (
    warehouseId: string,
    declaration: DeclarationRequest,
): Promise<DeclarationPreview> =>
    ReparoApi({
        method: 'POST',
        url: `/cm-sites/${warehouseId}/declare/preview`,
        body: JSON.stringify(declaration),
    });

// ─── Tracing ───────────────────────────────────────────────────

export interface TraceLink {
    batchId: string;
    batchCode: string | null;
    itemId: string;
    /** What a person calls it. Resolved by the server; the id stays for actions. */
    itemName?: string | null;
    itemType: string;
    quantity: number;
    productionEntryIds: string[];
}

/** One declaration: when, who, against what, and what moved. See the server's TraceRun. */
export interface TraceRun {
    productionEntryId: string;
    at: string;
    enteredBy: string | null;
    workOrderCode: string | null;
    productionOrderCode: string | null;
    produced: TraceLink[];
    consumed: TraceLink[];
}

export interface BatchTrace {
    batch: {
        batchId: string;
        code: string;
        itemId: string | null;
        /** What a person calls it. Resolved by the server; the id stays for actions. */
        itemName?: string | null;
        itemType: string | null;
        originType: string | null;
        originId: string | null;
        manufacturingDate: string | null;
        expiryDate: string | null;
        qcStatus: string | null;
    };
    balance: {
        in: number;
        out: number;
        /** From the stored balance, not derived — which is what lets the next field be non-zero. */
        onHand: number;
        unaccounted: number;
        balances: boolean;
    };
    /** Each declaration this batch took part in, in time order. */
    runs: TraceRun[];
    consumedFrom: TraceLink[];
    producedInto: TraceLink[];
    shipments: Array<{
        purchaseOrderId: string | null;
        workOrderId: string | null;
        quantity: number;
        date: string;
        warehouseId: string | null;
        /** This row UNDID a dispatch rather than being one. The screen signs and labels it. */
        isReversal: boolean;
    }>;
    otherExits: Array<{
        movementType: string;
        quantity: number;
        date: string;
        reasonCode: string | null;
        notes: string | null;
    }>;
    downstream: {
        /** Where certainty stops. Shown, never buried. */
        boundary: string;
        candidates: Array<{
            warehouseId: string;
            productId: string;
            quantity: number;
            date: string;
            movementType: string | null;
        }>;
    };
}

export interface TraceSearchHit {
    batchId: string;
    code: string;
    itemId: string | null;
    /** What a person calls it. Resolved by the server; the id stays for actions. */
    itemName?: string | null;
    itemType: string | null;
    originId: string | null;
}

/**
 * Both doors, one shape.
 *
 * A brand traces anything of its own; a manufacturer traces what passed through its own hands.
 * The scoping is the server's business — the screen is identical either way.
 */
export const searchBatchesByCode = async (
    code: string,
    asSite: boolean,
): Promise<{ batches: TraceSearchHit[] }> =>
    ReparoApi({
        method: 'GET',
        url: `${asSite ? '/vendor-portal' : '/cm-sites'}/trace/search?code=${encodeURIComponent(code)}`,
    });

export const getBatchTrace = async (batchId: string, asSite: boolean): Promise<BatchTrace> =>
    ReparoApi({
        method: 'GET',
        url: `${asSite ? '/vendor-portal' : '/cm-sites'}/trace/${batchId}`,
    });

/** Download the trace as a document. */
export const downloadTraceDocument = async (batchId: string, code: string): Promise<void> => {
    const url = await fetchPdfObjectUrl(`/cm-sites/trace/${batchId}/pdf`);
    const link = document.createElement('a');
    link.href = url;
    link.download = `trace-${code.replace(/[^\w.-]+/g, '_')}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
};

/**
 * The same batch register the manufacturer sees, read by the brand.
 *
 * Deliberately the same endpoint shape: two readers over one set of runs would eventually
 * disagree, and the first thing a brand does with this is check it against the spreadsheet
 * the manufacturer sent them.
 */
export const getSiteBatchRegister = async (
    warehouseId: string,
    itemType: 'product' | 'material' = 'product',
): Promise<{ batches: BatchRegisterRow[] }> =>
    ReparoApi({ method: 'GET', url: `/cm-sites/${warehouseId}/batches?itemType=${itemType}` });

/**
 * Batches to pick from when tracing.
 *
 * A convenience beside the search box, never a replacement: a recall code arrives from outside
 * the system and may name a batch this list does not carry, which is why the field still takes
 * free text.
 */
export const getTraceBatchOptions = async (
    asSite: boolean,
): Promise<{ batches: TraceSearchHit[] }> =>
    ReparoApi({
        method: 'GET',
        url: asSite ? '/vendor-portal/trace/batches' : '/cm-sites/trace/batches',
    });

/** Why a count disagreed with the book. Mirrors StockAdjustmentReason on the server. */
export const STOCK_ADJUSTMENT_REASONS = [
    { value: 'RECOUNT_HIGHER', label: 'A count found more' },
    { value: 'RECOUNT_LOWER', label: 'A count found less' },
    { value: 'MISKEYED_ENTRY', label: 'An earlier entry was wrong' },
    { value: 'DAMAGE_FOUND', label: 'Damage found on the shelf' },
    { value: 'OTHER', label: 'Something else' },
] as const;

export interface AdjustStockRequest {
    warehouseId: string;
    itemId: string;
    itemType: 'product' | 'material';
    /** Always positive. Direction is `incoming`, never a negative number. */
    quantity: number;
    /** True when the count found MORE than the book said. */
    incoming: boolean;
    reasonCode: string;
    batchId?: string;
    date?: string;
    notes?: string;
}

/**
 * Correct what a site is holding.
 *
 * Sites only, and the server enforces it: a correction at a WMS-synced warehouse would be
 * overwritten by the next sync, so the route refuses anything that is not a manufacturing site.
 */
export const adjustSiteStock = async (
    body: AdjustStockRequest,
): Promise<{ eventIds: string[] }> =>
    ReparoApi({
        method: 'POST',
        url: '/cm-sites/adjust',
        body: JSON.stringify(body),
    });
